import KatalLogger, { Level, LoggerConfig } from '@amzn/katal-logger';
import { recordAWSRumEvent } from 'src/analytics/CloudWatchRumClient';
import { getBrowserInfo, getPlatform, getTimezone } from 'src/utils/clientEnvUtils';
import { retrieveParameterFromSSM } from 'src/utils/ssm-parameter';

interface ApiEventLog {
  requestType: 'custom_query' | 'query' | 'mutation';
  requestName: string;
  requestStatus: 'API_SUCCESS' | 'API_FAILED';
  responseTime: number;
}

export let logger: KatalLogger;

/**
 * Validates the log level retrieved from SSM.
 * @param logLevel The log level to validate.
 * @returns The valid log level or Level.INFO if invalid.
 */
const validateLogLevel = (logLevel: string): Level => {
  if (Object.values(Level).includes(logLevel as Level)) {
    return logLevel as Level;
  }
  return Level.INFO;
};

export const getLogLevel = async (): Promise<Level> => {
  try {
    const params = '/xpt-fe/log-level';
    const result = await retrieveParameterFromSSM(params);
    return validateLogLevel(result);
  } catch (error) {
    return Level.INFO;
  }
};

export const configureKatalLogger = async (userAlias: string, displayName: string, isDev: boolean) => {
  const logThreshold = await getLogLevel();
  const { name: browserName, version: browserVersion } = getBrowserInfo();

  const katalLoggerConfig: LoggerConfig = {
    url: 'https://alpha.logger.xpt.device.finance.amazon.dev/v1/log',
    logThreshold: logThreshold,
    maxLogLineSize: 10000,
    logToConsole: true,
    // IMPORTANT: Do not modify this structure as it is directly mapped to the log table.
    context: {
      app: 'xpt-website',
      env: 'alpha',
      logType: 'general',
      userAlias: userAlias,
      givenName: displayName,
      isDev: isDev,
      userTimezone: getTimezone(),
      browser: browserName,
      browserVersion: browserVersion,
      platform: getPlatform()
    }
  };
  logger = new KatalLogger(katalLoggerConfig);
  logger.addPerformanceListener();
};

/**
 * Logs API events to AWS CloudWatch and records them in AWS RUM, including failures.
 * @param apiEvent - The API event log.
 * @param error - Optional. The error object captured when an API request fails.
 */
export const logApiEvent = (apiEvent: ApiEventLog, error?: any) => {
  const logPayload = {
    logType: 'api',
    apiEvent: {
      operation: apiEvent.requestType,
      operationName: apiEvent.requestName,
      requestStatus: apiEvent.requestStatus,
      responseTime: apiEvent.responseTime,
      ...(error && { failureDetails: error })
    }
  };

  // Log to AWS CloudWatch using the logger
  if (error) {
    logger.error(`Request Name - ${apiEvent.requestName}, Response Time - ${apiEvent.responseTime}ms`, logPayload);
  } else {
    logger.info(`Request Name - ${apiEvent.requestName}, Response Time - ${apiEvent.responseTime}ms`, logPayload);
  }

  // Record the event in AWS RUM
  recordAWSRumEvent(`${apiEvent.requestType}_${apiEvent.requestName}`, apiEvent.responseTime);
};

// IMPORTANT: Do not modify this structure as it is directly mapped to the log table.
export const entryLog = async () => {
  try {
    await logger.info(`User authenticated successfully!`, {
      logType: 'entry_log'
    });
  } catch (error) {
    console.error('Error logging entry:', error);
  }
};

/**
 * Logs user click events to AWS CloudWatch.
 * @param {string} action - The action that the user performed.
 * @param {string} page - The name of the page where the click event occurred.
 * @param {string} main_page - The main section of the application (e.g., Business Group, Admin Console, or Dev Console)
 */
export const logUserClick = (action: string, page: string, main_page: string) => {
  const logPayload = {
    logType: 'user_click_events',
    action,
    page,
    main_page
  };

  logger.info(`${action} in ${page} part of ${main_page}`, logPayload);
};
